import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AppointmentsHttpService, PrescriptionTypes, refreshPrescriptionsData } from '@lib/prescriptions/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';

import { PartnersService } from '@hm/core/services/partners.service';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';
import { formatDate } from '@lib/commons/util-functions/format-date.function';

import {
  AppointmentEventPlaceType,
  CreateAppointmentModalType,
  CreateLaboratoryOrderControls,
  LaboratoryScheduleSearch,
  LaboratoryWithScheduleResponse,
  PlanPrescriptionPayload,
  PlanningType,
  PrescriptionDetails,
  PrescriptionSource,
  ScheduleItem,
  TimeSlot,
} from '@lib/core/models/create-appointment.interface';
import { InfiniteScrollResponse } from '@lib/core/models/infinite-scroll-response.interface';
import { PartnerSearchModelV2, PartnerV2, ServiceType } from '@lib/core/models/partners.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

import { PrescriptionModalItemComponent } from '../../commons/modal-item/prescription-modal-item.component';
import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { TimeSlotsComponent } from '@lib/shared-ui/commons/time-slots/time-slots.component';
import { WarningMessageComponent } from '@lib/shared-ui/commons/warning-message/warning-message.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

export interface CreateLaboratoryOrderModalData {
  id?: string;
  type: CreateAppointmentModalType;
  userId: string;
  appointmentType: AppointmentTypes; // maybe can be removed and hardcoded as AppointmentTypes.LaboratoryService
  appointmentName: string;
  eventPlace?: AppointmentEventPlaceType;
  prescriptions: PrescriptionDetails[];
  startDate?: Date;
  partnerId?: string;
  partnerName?: string;
  address?: string;
  locationId?: string;
  doctorId?: string;
  price?: number;
}

@Component({
  selector: 'prescriptions-create-laboratory-order-modal',
  standalone: true,
  imports: [
    MatDialogContent,
    LoaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatError,
    MatFormField,
    MatLabel,
    MatOption,
    MatInput,
    TranslatePipe,
    MatDatepicker,
    MatDatepickerToggle,
    MatDatepickerInput,
    MatDialogActions,
    MatDialogClose,
    MatTimepickerInput,
    MatTimepicker,
    MatTimepickerToggle,
    MatSuffix,
    MatAutocomplete,
    MatAutocompleteTrigger,
    WarningMessageComponent,
    MatIcon,
    MatIconButton,
    TimeSlotsComponent,
    InfiniteScrollDirective,
    SpinLoadingComponent,
    PrescriptionModalItemComponent,
  ],
  templateUrl: './create-laboratory-order-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateLaboratoryOrderModalComponent implements OnInit {
  protected readonly data: CreateLaboratoryOrderModalData = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersService);
  private readonly createAppointmentService = inject(AppointmentsHttpService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogRef = inject(MatDialogRef<CreateLaboratoryOrderModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  protected readonly modalType = CreateAppointmentModalType;
  protected readonly prescriptionTypes = PrescriptionTypes;

  protected createLaboratoryOrderForm!: FormGroup<CreateLaboratoryOrderControls>;

  protected readonly loading = signal<boolean>(false);
  protected readonly partnerOptions = signal<PartnerV2[]>([]);
  protected readonly laboratoryOptions = signal<LaboratoryWithScheduleResponse[]>([]);
  protected readonly allLaboratoryOptions = signal<LaboratoryWithScheduleResponse[]>([]);
  protected readonly selectedPartner = signal<PartnerV2 | null>(null);
  protected readonly availableSlots = signal<TimeSlot[]>([]);
  protected readonly selectedSlot = signal<TimeSlot | null>(null);

  // Pagination tokens
  protected readonly partnersContinuationToken = signal<string | null>(null);

  // Search loading states
  protected readonly partnersLoading = signal<boolean>(false);
  protected readonly laboratoriesLoading = signal<boolean>(false);
  protected readonly slotsLoading = signal<boolean>(false);

  protected readonly userId = computed<string>(() => this.data.userId);
  protected readonly type = computed<CreateAppointmentModalType>(() => this.data.type);
  protected readonly partnerName = computed<string>(() => this.data.partnerName);
  protected readonly address = computed<string>(() => this.data.address);
  protected readonly startDate = computed<string>(() => formatDate(this.data.startDate, true));
  protected readonly hasIntegration = computed<boolean>(() => this.selectedPartner()?.integrationEnabled || false);
  protected readonly showOrderNumber = computed<boolean>(() => !this.hasIntegration());

  protected get fc() {
    return this.createLaboratoryOrderForm.controls as CreateLaboratoryOrderControls;
  }

  ngOnInit() {
    this.initModal();
  }

  private initModal() {
    this.initForm();
    this.initPartnersSearch();
    this.initLaboratoriesSearch();

    if (this.data.partnerId) this.getPartnerById(this.data.partnerId);
    else this.getPartners();
  }

  private initForm() {
    this.createLaboratoryOrderForm = this.fb.group<CreateLaboratoryOrderControls>({
      orderName: this.fb.control(this.data.appointmentName, [Validators.required]),
      prescriptionIds: this.fb.control(this.data.prescriptions?.map(p => p.id) || [], [Validators.required]),
      partnerId: this.fb.control(null, [Validators.required]),
      partnerName: this.fb.control(null),
      locationId: this.fb.control(null, [Validators.required]),
      locationName: this.fb.control(null),
      date: this.fb.control(null, [Validators.required]),
      startTime: this.fb.control(null, [Validators.required]),
      endTime: this.fb.control(null, [Validators.required]),
      price: this.fb.control(null, [Validators.required]),
      orderNumber: this.fb.control(null),
    });
  }

  private initPartnersSearch() {
    this.fc.partnerName.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchTerm => {
          const searchString = typeof searchTerm === 'string' ? searchTerm.trim() : '';

          if (!searchString && this.fc.partnerId.value) return [];
          if (!searchString) {
            this.partnersLoading.set(true);
            this.partnersContinuationToken.set(null);

            const partnerSearchPayload: PartnerSearchModelV2 = {
              pageSize: DEFAULT_PAGE_SIZE,
              active: true,
              searchTerm: null,
              supportedServices: [ServiceType.Laboratory],
            };

            return this.partnersService.searchPartnersV2(partnerSearchPayload);
          }

          this.partnersLoading.set(true);
          this.partnerOptions.set([]);
          this.partnersContinuationToken.set(null);

          return this.searchPartners(searchString);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: InfiniteScrollResponse<PartnerV2>) => {
          if (result && result.items) {
            this.partnerOptions.set(result.items);
            this.partnersContinuationToken.set(result.continuationToken || null);
          }
          this.partnersLoading.set(false);
        },
        error: () => {
          this.partnerOptions.set([]);
          this.partnersLoading.set(false);
        },
      });
  }

  private searchPartners(searchTerm: string, continuationToken?: string) {
    const partnerSearchPayload: PartnerSearchModelV2 = {
      pageSize: DEFAULT_PAGE_SIZE,
      active: true,
      searchTerm: searchTerm || null,
      supportedServices: [ServiceType.Laboratory],
      continuationToken: continuationToken,
    };
    if (!continuationToken) delete partnerSearchPayload.continuationToken;

    return this.partnersService.searchPartnersV2(partnerSearchPayload);
  }

  private getPartners() {
    this.partnersLoading.set(true);

    const partnerSearchPayload: PartnerSearchModelV2 = {
      pageSize: DEFAULT_PAGE_SIZE,
      active: true,
      searchTerm: null,
      supportedServices: [ServiceType.Laboratory],
    };

    return this.partnersService.searchPartnersV2(partnerSearchPayload).subscribe({
      next: result => {
        this.partnerOptions.set(result.items);
        this.partnersContinuationToken.set(result.continuationToken || null);
        this.partnersLoading.set(false);
      },
      error: () => this.partnersLoading.set(false),
    });
  }

  private getPartnerById(partnerId: string) {
    this.loading.set(true);

    return this.partnersService
      .getPartnerByIdV2(partnerId)
      .pipe(
        tap(result => {
          if (result) {
            this.partnerOptions.set([result]);
            this.selectPartner(result);
          }
        })
      )
      .subscribe({
        next: () => this.loading.set(false),
        error: () => {
          this.loading.set(false);
          this.snackBar.open(this.translateService.instant('partner.message.chatPartnerUnavailable'), 'OK', { duration: 5000 });
          this.getPartners();
        },
      });
  }

  protected fetchPartnersNextPage() {
    if (!this.partnersContinuationToken()) return;

    this.partnersLoading.set(true);
    const currentSearchTerm = this.fc.partnerName.value;

    this.searchPartners(currentSearchTerm, this.partnersContinuationToken()!).subscribe({
      next: (result: InfiniteScrollResponse<PartnerV2>) => {
        this.partnerOptions.update(partners => [...partners, ...result.items]);
        this.partnersContinuationToken.set(result.continuationToken || null);
        this.partnersLoading.set(false);
      },
      error: () => this.partnersLoading.set(false),
    });
  }

  private initLaboratoriesSearch() {
    this.fc.locationName.valueChanges
      .pipe(
        debounceTime(300),
        filter(() => !!this.fc.partnerId.value),
        tap(searchTerm => {
          const searchString = typeof searchTerm === 'string' ? searchTerm : '';

          if (!searchString.trim()) this.laboratoryOptions.set(this.allLaboratoryOptions());
          else {
            const filtered = this.allLaboratoryOptions().filter(
              lab => lab.locationName.toLowerCase().includes(searchString.toLowerCase()) || lab.locationAddress.toLowerCase().includes(searchString.toLowerCase())
            );
            this.laboratoryOptions.set(filtered);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getLaboratories(): void {
    this.laboratoriesLoading.set(true);
    if (!this.fc.partnerId.value) return;

    const schedulePayload: LaboratoryScheduleSearch = {
      userId: this.data.userId,
      prescriptionIds: this.data.prescriptions?.map(p => p.id) || [],
      partnerId: this.fc.partnerId.value,
    };

    this.createAppointmentService.searchLaboratoriesSchedule(schedulePayload).subscribe({
      next: (laboratories: LaboratoryWithScheduleResponse[]) => {
        this.allLaboratoryOptions.set(laboratories);
        this.laboratoryOptions.set(laboratories);
        this.laboratoriesLoading.set(false);

        if (this.data.locationId) {
          const targetLocation = laboratories.find(lab => lab.locationId === this.data.locationId);

          if (targetLocation) this.selectLaboratory(targetLocation);
          else console.warn('⚠️ Target location not found in laboratories list:', this.data.locationId);
        }
      },
      error: () => {
        this.allLaboratoryOptions.set([]);
        this.laboratoryOptions.set([]);
        this.laboratoriesLoading.set(false);
      },
    });
  }

  protected selectPartnerByName(partnerName: string) {
    const partner = this.partnerOptions().find(p => p.name === partnerName);
    if (partner) this.selectPartner(partner);
  }

  protected selectPartner(partner: PartnerV2) {
    this.fc.partnerId.setValue(partner.id);
    this.fc.partnerName.setValue(partner.name, { emitEvent: false });
    this.selectedPartner.set(partner);
    this.availableSlots.set([]);
    this.selectedSlot.set(null);

    this.clearLaboratoriesSearch();
  }

  protected selectLocationById(locationId: string) {
    const laboratory = this.laboratoryOptions().find(l => l.locationId === locationId);
    if (laboratory) this.selectLaboratory(laboratory);
  }

  protected selectLaboratory(laboratory: LaboratoryWithScheduleResponse) {
    const displayName = `${laboratory.locationName} - ${laboratory.locationAddress}`;

    this.fc.locationId.setValue(laboratory.locationId);
    this.fc.locationName.setValue(displayName, { emitEvent: false });

    // For integrated partners, generate day slots from schedule and timeSlots
    if (this.selectedPartner()?.integrationEnabled) {
      if (laboratory.schedule && laboratory.timeSlots) {
        const daySlots = this.generateDaySlots(laboratory.schedule, laboratory.timeSlots);
        this.availableSlots.set(daySlots);
      } else {
        this.availableSlots.set([]);
      }
    } else {
      // For non-integrated partners, clear slots (they will use date/time pickers)
      this.availableSlots.set([]);
    }
  }

  private generateDaySlots(schedule: ScheduleItem[], timeSlots: TimeSlot[]): TimeSlot[] {
    const daySlots: TimeSlot[] = [];
    const now = new Date();
    const today = now.toDateString();

    // Group timeSlots by date
    const slotsByDate = new Map<string, TimeSlot[]>();
    timeSlots.forEach(slot => {
      const slotDate = new Date(slot.startTime).toDateString();
      if (!slotsByDate.has(slotDate)) {
        slotsByDate.set(slotDate, []);
      }
      slotsByDate.get(slotDate)!.push(slot);
    });

    // Create one representative slot per day that has available slots
    schedule.forEach(scheduleItem => {
      const scheduleDate = new Date(scheduleItem.startTime).toDateString();
      const slotsForDay = slotsByDate.get(scheduleDate);

      if (slotsForDay && slotsForDay.length > 0) {
        // Check if this is today and working hours have passed
        if (scheduleDate === today) {
          const scheduleEndTime = new Date(scheduleItem.endTime);

          // If working hours end time has passed, skip this day
          if (now > scheduleEndTime) return;
        }

        // Use the first available slot for this day as representative
        const firstSlot = slotsForDay[0];
        const workingHours =
          scheduleItem.workingHours && scheduleItem.workingHours.length > 0
            ? scheduleItem.workingHours[0].displayHours
            : `${this.formatSlotTime(firstSlot.startTime)} - ${this.formatSlotTime(firstSlot.endTime)}`;

        // Create a day-level slot
        const daySlot: TimeSlot = {
          startTime: firstSlot.startTime,
          endTime: firstSlot.endTime,
          duration: workingHours,
          durationMinutes: firstSlot.durationMinutes,
          available: true,
        };

        daySlots.push(daySlot);
      }
    });

    return daySlots;
  }

  protected clearPartnersSearch() {
    this.fc.partnerName.setValue('', { emitEvent: false });
    this.fc.partnerId.setValue(null);
    this.partnerOptions.set([]);
    this.partnersContinuationToken.set(null);
    this.getPartners();
  }

  protected clearLaboratoriesSearch() {
    this.fc.locationName.setValue('', { emitEvent: false });
    this.fc.locationId.setValue(null);
    this.laboratoryOptions.set(this.allLaboratoryOptions());

    if (this.fc.partnerId.value) this.getLaboratories();
  }

  private combineDateTime(date: Date, time: Date): string {
    date.setHours(time.getHours(), time.getMinutes(), 0, 0);

    return date.toISOString();
  }

  protected selectSlot(slot: TimeSlot) {
    this.selectedSlot.set(slot);

    const startDate = new Date(slot.startTime);
    const endDate = new Date(slot.endTime);

    this.fc.date.setValue(startDate);
    this.fc.startTime.setValue(startDate);
    this.fc.endTime.setValue(endDate);
  }

  protected getSelectedSlotWorkingHours(): string {
    if (!this.selectedSlot()) return '';

    const slot = this.selectedSlot()!;
    const startTime = this.formatSlotTime(slot.startTime);
    const endTime = this.formatSlotTime(slot.endTime);

    // For day-level slots, duration contains working hours
    if (typeof slot.duration === 'string' && slot.duration.includes('-')) return slot.duration;

    return `${startTime} - ${endTime}`;
  }

  private formatSlotTime(dateString: string): string {
    const date = new Date(dateString);

    return date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  }

  protected isSubmitDisabled(): boolean {
    const baseFieldsValid = this.fc.partnerId.valid && this.fc.locationId.valid && this.fc.price.valid;

    if (this.hasIntegration()) return !baseFieldsValid || !this.selectedSlot();
    else return !baseFieldsValid || !this.fc.date.valid || !this.fc.startTime.valid || !this.fc.endTime.valid;
  }

  protected submit() {
    this.createLaboratoryOrder();
  }

  private createLaboratoryOrder() {
    this.loading.set(true);

    let startTime: string;
    let endTime: string;

    if (this.hasIntegration() && this.selectedSlot()) {
      const slot = this.selectedSlot()!;
      const currentLaboratory = this.laboratoryOptions().find(l => l.locationId === this.fc.locationId.value);

      if (currentLaboratory && currentLaboratory.schedule) {
        const slotDate = new Date(slot.startTime).toDateString();
        const scheduleItem = currentLaboratory.schedule.find(schedule => new Date(schedule.startTime).toDateString() === slotDate);

        if (scheduleItem) {
          startTime = new Date(scheduleItem.startTime).toISOString();
          endTime = new Date(scheduleItem.endTime).toISOString();
        } else {
          // Fallback to slot times if schedule item not found
          startTime = slot.startTime;
          endTime = slot.endTime;
        }
      } else {
        // Fallback to slot times if no schedule
        startTime = slot.startTime;
        endTime = slot.endTime;
      }
    } else {
      startTime = this.combineDateTime(this.fc.date.value!, this.fc.startTime.value!);
      endTime = this.combineDateTime(this.fc.date.value!, this.fc.endTime.value!);
    }

    const payload: PlanPrescriptionPayload = {
      $type: PlanningType.Laboratory,
      userId: this.data.userId,
      partnerId: this.fc.partnerId.value!,
      locationId: this.fc.locationId.value!,
      prescriptions: this.data.prescriptions || [],
      startTime,
      endTime,
      source: PrescriptionSource.Manual,
      price: this.fc.price.value!,
      orderNumber: this.showOrderNumber() ? this.fc.orderNumber.value || undefined : undefined,
      eventPlace: AppointmentEventPlaceType.Hospital,
    };

    this.createAppointmentService.createAppointment(payload).subscribe({
      next: () => {
        this.store.dispatch(refreshPrescriptionsData());

        this.snackBar.open(this.translateService.instant('laboratoryOrder.message.laboratoryOrderCreatedSuccess'), 'OK', { duration: 5000 });
        this.loading.set(false);
        this.dialogRef.close(payload);
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('laboratoryOrder.message.laboratoryOrderCreateError'), 'OK', { duration: 5000 });
        this.loading.set(false);
      },
    });
  }
}
